import { createReducer, on } from '@ngrx/store';

import { getBrowserLang } from '@ngneat/transloco';
import { isEqual as _isEqual } from 'lodash-es';

import id128 from 'id128';

import { Permission, Screen } from '@core/interfaces';
import { currentTimeInSeconds } from '@util/list';

import { settings } from '@environment';

import { AuthActions } from '@auth/state';

const { Ulid } = id128;

export interface AuthState {
  authenticated: boolean;
  changePending: boolean;
  id: string;
  permissions: Permission[];
  sessionTimeout: boolean;
  sessionTimeoutTime: number;
  theme: string;
  app: {
    env: string;
  };
  screens: {
    currentScreen: Screen;
    distinctPreviousScreen: Screen;
    previousScreen: Screen;
  };
  user: {
    allcusts: string;
    chgfacility: string;
    custid: string;
    env: string;
    facility: string;
    groupid: string;
    language: string;
    nameid: string;
    customerAux: {
      hasPassThru: string;
    };
  };
}

function initialLanguage() {
  const clientBrowserLanguage = getBrowserLang();
  const supportedLanguages: string[] = [...settings.supportedLanguages];

  if (supportedLanguages.includes(clientBrowserLanguage)) {
    return clientBrowserLanguage;
  } else {
    return settings.defaultLanguage;
  }
}

export const initialState: AuthState = {
  authenticated: false,
  changePending: false,
  id: Ulid.generate().toRaw(),
  permissions: [],
  sessionTimeout: false,
  sessionTimeoutTime: null,
  theme: 'light',
  app: {
    env: null,
  },
  screens: {
    currentScreen: null,
    distinctPreviousScreen: null,
    previousScreen: null,
  },
  user: {
    allcusts: null,
    chgfacility: null,
    custid: null,
    groupid: null,
    env: null,
    facility: null,
    language: initialLanguage(),
    nameid: null,
    customerAux: {
      hasPassThru: null,
    },
  },
};

export const reducer = createReducer(
  initialState,

  //
  // login
  //

  on(
    AuthActions.Login,
    (state): AuthState => ({
      ...state,
      authenticated: false,
      changePending: false,
      permissions: [],
      sessionTimeout: false,
      sessionTimeoutTime: null,
      screens: {
        ...state.screens,
      },
      user: {
        ...state.user,
        language: state.user.language,
      },
    })
  ),

  on(
    AuthActions.LoginFromTimeout,
    (state): AuthState => ({
      ...state,
    })
  ),

  on(
    AuthActions.LoginSuccess,
    AuthActions.LoginFromTimeoutSuccess,
    (state, action): AuthState => ({
      ...state,
      authenticated: true,
      sessionTimeout: false,
      sessionTimeoutTime: null,
      theme: action.payload.theme,
      screens: {
        ...state.screens,
      },
      user: {
        allcusts: action.payload.allcusts,
        chgfacility: action.payload.chgfacility,
        custid: action.payload.custid,
        env: action.payload.env,
        facility: action.payload.facility,
        groupid: action.payload.groupid,
        language: action.payload.language ?? state.user.language,
        nameid: action.payload.nameid,
        customerAux: {
          hasPassThru: action.payload.customer_aux[0]?.expanded_websynapse_fields ?? 'N',
        },
      },
    })
  ),

  on(
    AuthActions.LoginFailure,
    (state): AuthState => ({
      ...state,
      authenticated: false,
      user: {
        ...initialState.user,
        language: state.user.language,
      },
      screens: {
        ...state.screens,
      },
    })
  ),

  //
  // login from broadcast
  //

  on(
    AuthActions.LoginFromBroadcast,
    (state, action): AuthState => ({
      ...state,
      id: Ulid.generate().toRaw(),
      authenticated: true,
      sessionTimeout: action.payload.sessionTimeout,
      sessionTimeoutTime: action.payload.sessionTimeoutTime,
      changePending: action.payload.changePending,
      permissions: action.payload.permissions,
      theme: action.payload.theme,
      screens: {
        ...state.screens,
      },
      user: {
        allcusts: action.payload.user.allcusts,
        chgfacility: action.payload.user.chgfacility,
        custid: action.payload.user.custid,
        env: action.payload.user.env,
        facility: action.payload.user.facility,
        groupid: action.payload.user.groupid,
        language: action.payload.user.language,
        nameid: action.payload.user.nameid,
        customerAux: {
          ...action.payload.user.customerAux,
        },
      },
    })
  ),

  //
  // log out
  //

  on(
    AuthActions.Logout,
    (state): AuthState => ({
      ...initialState,
      id: Ulid.generate().toRaw(),
      theme: state.theme,
      user: {
        ...initialState.user,
        nameid: state.user.nameid,
        language: state.user.language,
      },
      screens: {
        currentScreen: null,
        distinctPreviousScreen: null,
        previousScreen: null,
      },
    })
  ),

  on(
    AuthActions.Permissions,
    (state): AuthState => ({
      ...state,
      permissions: [],
    })
  ),
  on(
    AuthActions.PermissionsSuccess,
    (state, action): AuthState => ({
      ...state,
      permissions: [...action.payload.userdetail],
    })
  ),

  //
  // set authenticated state
  //

  on(
    AuthActions.SetAuthenticated,
    (state, action): AuthState => ({
      ...state,
      authenticated: action.authenticated,
    })
  ),

  //
  // session timeout
  //

  on(AuthActions.SessionPingFailure, (state) => ({
    ...state,
    sessionTimeout: true,
    sessionTimeoutTime: currentTimeInSeconds(),
  })),

  on(AuthActions.SetSessionTimeout, (state, action) => ({
    ...state,
    sessionTimeout: action.timeout,
    sessionTimeoutTime: action.timeout ? currentTimeInSeconds() : null,
  })),

  //
  // change language
  //

  on(
    AuthActions.ChangeLanguage,
    (state, action): AuthState => ({
      ...state,
      user: {
        ...state.user,
        language: action.language,
      },
    })
  ),

  on(
    AuthActions.ChangeLanguageFromBroadcast,
    (state, action): AuthState => ({
      ...state,
      user: {
        ...state.user,
        language: action.language,
      },
    })
  ),

  //
  // change customer
  //

  on(
    AuthActions.ChangeCustomer,
    (state): AuthState => ({
      ...state,
      changePending: true,
    })
  ),

  on(
    AuthActions.ChangeCustomerSuccess,
    AuthActions.ChangeCustomerFromBroadcast,
    (state, action): AuthState => ({
      ...state,
      changePending: false,
      user: {
        ...state.user,
        custid: action.payload.custid,
        allcusts: action.payload.allcusts,
        chgfacility: action.payload.chgfacility,
        facility: action.payload.facility,
        customerAux: {
          hasPassThru: action.payload.customer_aux[0]?.expanded_websynapse_fields ?? 'N',
        },
      },
    })
  ),

  on(
    AuthActions.ChangeCustomerFailure,
    (state): AuthState => ({
      ...state,
      changePending: false,
    })
  ),

  //
  // change facility
  //

  on(
    AuthActions.ChangeFacility,
    (state): AuthState => ({
      ...state,
      changePending: true,
    })
  ),

  on(
    AuthActions.ChangeFacilitySuccess,
    AuthActions.ChangeFacilityFromBroadcast,
    (state, action): AuthState => ({
      ...state,
      changePending: false,
      user: {
        ...state.user,
        facility: action.payload.facility,
        allcusts: action.payload.allcusts,
        chgfacility: action.payload.chgfacility,
        custid: action.payload.custid,
      },
    })
  ),

  on(
    AuthActions.ChangeFacilityFailure,
    (state): AuthState => ({
      ...state,
      changePending: false,
    })
  ),

  //
  // change screen
  //

  on(AuthActions.ChangeScreen, (state, action) => {
    const currentScreen = { ...state.screens.currentScreen };
    const previousScreen = { ...state.screens.distinctPreviousScreen };

    return {
      ...state,
      screens: {
        ...state.screens,
        distinctPreviousScreen: _isEqual(currentScreen, action.screen) ? previousScreen : currentScreen,
        previousScreen: { ...state.screens.currentScreen },
        currentScreen: action.screen,
      },
    };
  }),

  //
  // change theme
  //

  on(
    AuthActions.ChangeTheme,
    AuthActions.ChangeThemeFromBroadcast,
    (state, action): AuthState => ({
      ...state,
      theme: action.theme,
    })
  ),

  //
  // session id
  //

  on(
    AuthActions.SetSessionId,
    (state, action): AuthState => ({
      ...state,
      id: action.id,
    })
  ),

  //
  // application environment
  //

  on(
    AuthActions.SetApplicationEnvironment,
    (state, action): AuthState => ({
      ...state,
      app: {
        ...state.app,
        env: action.environment,
      },
    })
  )
);
